var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * BulkPrintProgressModal
 * Displays progress for a sequential bulk label print job (pallet + cartons).
 * Driven by useBulkLabelPrint's `progress` state.
 */
import React from 'react';
import { Modal, Progress } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckCircleFilled, PrinterOutlined, WarningFilled } from '@ant-design/icons';
import ButtonAnt from '@fp-suite/core-ui/src/components/ButtonAnt';
import { useSelector } from 'react-redux';
import { selectUserPreferences } from '../../../redux/packing';
var formatItemLabel = function (item, t) {
    if (!item)
        return '';
    if (item.displayLabel)
        return item.displayLabel;
    var kindLabel = item.kind === 'pallet' ? t('Pallet') : t('Carton');
    return kindLabel + " " + item.containerId;
};
export var BulkPrintProgressModal = function (_a) {
    var visible = _a.visible, progress = _a.progress, onClose = _a.onClose, onCancel = _a.onCancel, onRetryFailed = _a.onRetryFailed;
    var t = useTranslation().t;
    var userPreferences = useSelector(selectUserPreferences);
    var hasLabelPrinter = Boolean(userPreferences['qz.printer.label.active']);
    var status = progress.status, total = progress.total, completed = progress.completed, current = progress.current, failed = progress.failed;
    var percent = total === 0 ? 0 : Math.round((completed / total) * 100);
    var isRunning = status === 'printing';
    var isDone = status === 'done';
    var hasFailures = status === 'error' || failed.length > 0;
    var isCancelled = status === 'cancelled';
    var headline = isDone
        ? t('All labels printed')
        : isCancelled
            ? t('Printing cancelled')
            : hasFailures && !isRunning
                ? t('Some labels failed to print')
                : hasLabelPrinter
                    ? t('Sending labels to printer…')
                    : t('Downloading labels…');
    var progressStatus = isRunning
        ? 'active'
        : isDone
            ? 'success'
            : hasFailures
                ? 'exception'
                : 'normal';
    return (_jsx(Modal, __assign({ title: t('Printing Labels'), visible: visible, footer: null, closable: !isRunning, maskClosable: false, keyboard: !isRunning, onCancel: function () {
            if (isRunning)
                return;
            onClose();
        }, width: '440px' }, { children: _jsxs("div", __assign({ className: 'space-y-4' }, { children: [_jsx("div", __assign({ className: 'flex items-center justify-center py-2' }, { children: isDone ? (_jsx(CheckCircleFilled, { style: { fontSize: '40px', color: '#52c41a' } }, void 0)) : hasFailures && !isRunning ? (_jsx(WarningFilled, { style: { fontSize: '40px', color: '#faad14' } }, void 0)) : (_jsx(PrinterOutlined, { style: { fontSize: '40px', color: '#1890ff' } }, void 0)) }), void 0), _jsxs("div", __assign({ className: 'text-center' }, { children: [_jsx("p", __assign({ className: 'text-sm font-medium' }, { children: headline }), void 0), _jsxs("p", __assign({ className: 'mt-1 text-xs text-gray-500' }, { children: [completed, " ", t('of'), " ", total] }), void 0)] }), void 0), _jsx(Progress, { percent: percent, status: progressStatus, showInfo: false }, void 0), isRunning && current && (_jsxs("div", __assign({ className: 'rounded bg-gray-50 p-3 text-center text-xs text-gray-700' }, { children: [t('Current'), ": ", formatItemLabel(current, function (k) { return t(k); })] }), void 0)), failed.length > 0 && !isRunning && (_jsxs("div", __assign({ className: 'rounded border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800' }, { children: [_jsxs("div", __assign({ className: 'mb-1 font-medium' }, { children: [failed.length, " ", t('label(s) failed'), ":"] }), void 0), _jsx("ul", __assign({ className: 'list-disc space-y-0.5 pl-5' }, { children: failed.map(function (f) { return (_jsx("li", { children: formatItemLabel(f, function (k) { return t(k); }) }, f.kind + "-" + f.containerId)); }) }), void 0)] }), void 0)), _jsxs("div", __assign({ className: 'flex justify-end gap-2 pt-1' }, { children: [isRunning && (_jsx(ButtonAnt, { buttonText: String(t('Cancel')), onClick: onCancel }, void 0)), !isRunning && failed.length > 0 && (_jsx(ButtonAnt, { buttonText: String(t('Retry failed')), type: 'primary', onClick: onRetryFailed }, void 0)), !isRunning && (_jsx(ButtonAnt, { buttonText: String(t('Close')), type: failed.length > 0 ? 'default' : 'primary', onClick: onClose }, void 0))] }), void 0)] }), void 0) }), void 0));
};
export default BulkPrintProgressModal;
